import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { logout } from '../redux/authSlice';
import {
  LayoutDashboard, Package, FolderTree,
  ShoppingCart, LogOut, Plus, List, MessageSquare, Menu, X, Settings
} from 'lucide-react';

const AdminLayout = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const admin = useSelector(state => state.auth.admin);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/admin/login');
  };

  const menuItems = [
    { name: 'الرئيسية',      path: '/admin',            icon: <LayoutDashboard size={20} /> },
    { name: 'الطلبيات',      path: '/admin/orders',      icon: <ShoppingCart size={20} /> },
    { name: 'إدارة المنتجات', path: '/admin/products',    icon: <Package size={20} /> },
    { name: 'إضافة منتج',    path: '/admin/addProduct',  icon: <Plus size={20} /> },
    { name: 'إدارة الأقسام', path: '/admin/categories',  icon: <List size={20} /> },
    { name: 'إضافة قسم',     path: '/admin/addCategory', icon: <FolderTree size={20} /> },
    { name: 'التعليقات',     path: '/admin/comments',    icon: <MessageSquare size={20} /> },
    { name: 'الإعدادات',     path: '/admin/settings',    icon: <Settings size={20} /> },
  ];

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex min-h-screen bg-white" dir="rtl">

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        w-64 bg-white border-l border-gray-400 flex flex-col fixed top-0 right-0 h-full z-30
        transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}
        md:translate-x-0
      `}>
        <div className="p-6 border-b border-gray-400 flex items-center justify-between">
          <div className="font-black text-emerald-600 text-xl uppercase tracking-tighter">
            BEN-MOUSSA <span className="text-gray-900 font-medium text-xs block">DASHBOARD</span>
          </div>
          <button onClick={closeSidebar} className="md:hidden p-1 text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 mt-4 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`flex items-center gap-3 px-5 py-4 rounded-2xl font-black transition-all border-2 ${
                  isActive
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'text-gray-500 border-transparent hover:border-gray-400 hover:text-gray-900'
                }`}
              >
                {item.icon}
                <span className="text-sm">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-400">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-5 py-4 w-full text-red-600 font-black border-2 border-transparent hover:border-red-600 rounded-2xl transition-all"
          >
            <LogOut size={20} />
            <span className="text-sm">تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Content Area */}
      <main className="flex-1 mr-0 md:mr-64 bg-white min-h-screen w-full overflow-x-hidden">
        {/* Header */}
        <header className="h-16 md:h-24 bg-white border-b border-gray-400 flex items-center justify-between px-4 md:px-10 sticky top-0 z-[5]">
          <div className="flex items-center gap-3">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50"
            >
              <Menu size={20} />
            </button>
            <h1 className="text-base md:text-xl font-black text-gray-900">
              {menuItems.find(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))?.name || 'لوحة التحكم'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-left" dir="ltr">
              <p className="text-sm font-black text-gray-900">{admin?.username || 'Admin'}</p>
              <p className="text-[10px] font-bold text-gray-400">Main Admin</p>
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 bg-white border-2 border-gray-400 rounded-2xl flex items-center justify-center text-emerald-600 font-black text-base md:text-lg">
              {(admin?.username?.[0] || 'A').toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
